import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import {
  isItemAuthorityCommandsFeatureEnabled,
  isItemAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const SCHEMA_VERSION = 1;
// Matches the item command contract (functions/itemsAuthority/contracts.js).
export const MAX_ITEM_SUMMARY_LENGTH = 5000;
// Placement reads (feed and item page) ask for version 2, which adds the
// organisation currency symbol to each listing. The server echoes the version.
const PLACEMENT_READ_SCHEMA_VERSION = 2;
const CURRENCY_SYMBOL_PATTERN = /^\S(?:.{0,14}\S)?$/u;
const PAGE_SIZE = 25;
const MAX_MEDIA_ASSETS = 8;
const MAX_LISTING_STOCK = 1_000_000_000;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const ITEM_ID_PATTERN = /^[a-f0-9]{64}$/;
const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const ASSET_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;
const CURRENCY_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{0,15}$/;
const ATOMIC_AMOUNT_PATTERN = /^(?:0|[1-9][0-9]{0,39})$/;
const CURSOR_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;

const ITEM_KINDS = new Set(["product", "service"]);
const PUBLICATION_AUDIENCES = new Set(["organisation", "public"]);
const PUBLICATION_STATES = new Set(["active", "withdrawn"]);
const LISTING_STATES = new Set(["active", "paused", "sold-out"]);
const TRADE_SCOPES = new Set(["organisation", "cross-org"]);
const FEED_SCOPES = new Set([
  "open",
  "organisations",
  "organisation-public",
  "follows",
]);
const PLACEMENT_ACTIONS = new Set([
  "publish",
  "change-audience",
  "withdraw",
  "list",
  "pause",
]);

export class ItemAuthorityServiceError extends Error {
  constructor(code = "unknown") {
    super("Items could not be loaded or changed.");
    this.name = "ItemAuthorityServiceError";
    this.code = code;
  }
}

function fail(code) {
  throw new ItemAuthorityServiceError(code);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, expectedKeys) {
  if (!isPlainRecord(value)) return false;
  const ownKeys = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const actualKeys = ownKeys.filter((key) => typeof key === "string").sort();
  const sortedExpectedKeys = [...expectedKeys].sort();
  return (
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        descriptors[key]?.enumerable === true &&
        Object.prototype.hasOwnProperty.call(descriptors[key], "value")
    ) &&
    actualKeys.length === sortedExpectedKeys.length &&
    actualKeys.every((key, index) => key === sortedExpectedKeys[index])
  );
}

function requireExactKeys(value, expectedKeys, code = "invalid-argument") {
  if (!hasExactKeys(value, expectedKeys)) fail(code);
  return value;
}

function hasOnlyKeys(value, allowedKeys) {
  if (!isPlainRecord(value)) return false;
  const allowed = new Set(allowedKeys);
  const ownKeys = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  return ownKeys.every(
    (key) =>
      typeof key === "string" &&
      allowed.has(key) &&
      descriptors[key]?.enumerable === true &&
      Object.prototype.hasOwnProperty.call(descriptors[key], "value")
  );
}

function normalizeError(error) {
  if (error instanceof ItemAuthorityServiceError) return error;
  return new ItemAuthorityServiceError(
    String(error?.code || "unknown")
      .split("/")
      .pop()
  );
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument");
  }
  return value;
}

function normalizeRevision(
  value,
  { minimum = 0, code = "invalid-argument" } = {}
) {
  if (!Number.isSafeInteger(value) || value < minimum) fail(code);
  return value;
}

function normalizeItemId(value, code = "invalid-argument") {
  if (typeof value !== "string" || !ITEM_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeOrgId(value, code = "invalid-argument") {
  if (typeof value !== "string" || !ORG_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeAssetId(value, code = "invalid-argument") {
  if (typeof value !== "string" || !ASSET_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeText(
  value,
  { minimum = 0, maximum, trim = true, code = "invalid-argument" } = {}
) {
  if (typeof value !== "string") fail(code);
  const normalized = trim ? value.trim() : value;
  if (
    normalized.length < minimum ||
    normalized.length > maximum ||
    CONTROL_CHARACTERS.test(normalized) ||
    (!trim && normalized !== normalized.trim())
  ) {
    fail(code);
  }
  return normalized;
}

function normalizeMediaAssetIds(value, code = "invalid-argument") {
  if (!Array.isArray(value) || value.length > MAX_MEDIA_ASSETS) fail(code);
  const assetIds = value.map((assetId) => normalizeAssetId(assetId, code));
  if (new Set(assetIds).size !== assetIds.length) fail(code);
  return Object.freeze(assetIds);
}

function normalizeItem(value, code = "invalid-argument") {
  requireExactKeys(
    value,
    ["kind", "title", "summary", "mediaAssetIds", "originOrgId"],
    code
  );
  if (!ITEM_KINDS.has(value.kind)) fail(code);
  return Object.freeze({
    kind: value.kind,
    title: normalizeText(value.title, { minimum: 1, maximum: 120, code }),
    summary: normalizeText(value.summary, { maximum: MAX_ITEM_SUMMARY_LENGTH, code }),
    mediaAssetIds: normalizeMediaAssetIds(value.mediaAssetIds, code),
    originOrgId:
      value.originOrgId === null
        ? null
        : normalizeOrgId(value.originOrgId, code),
  });
}

function normalizeCursor(value, code = "invalid-argument") {
  if (value === null) return null;
  if (
    typeof value !== "string" ||
    value.length < 32 ||
    value.length > 2048 ||
    !CURSOR_PATTERN.test(value)
  ) {
    fail(code);
  }
  return value;
}

function normalizePageSize(value) {
  if (!Number.isSafeInteger(value) || value < 1 || value > PAGE_SIZE) {
    fail("invalid-argument");
  }
  return value;
}

function normalizeAtomicAmount(
  value,
  { allowZero = false, code = "invalid-argument" } = {}
) {
  if (
    typeof value !== "string" ||
    !ATOMIC_AMOUNT_PATTERN.test(value) ||
    (!allowZero && value === "0")
  ) {
    fail(code);
  }
  return value;
}

function normalizeStock(
  value,
  { allowZero = false, code = "invalid-argument" } = {}
) {
  if (
    !Number.isSafeInteger(value) ||
    value < (allowZero ? 0 : 1) ||
    value > MAX_LISTING_STOCK
  ) {
    fail(code);
  }
  return value;
}

function normalizeCreateIntent(value) {
  requireExactKeys(value, ["operationId", "item"]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: 0,
    item: normalizeItem(value.item),
  });
}

function normalizeUpdateIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "itemId",
    "item",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    itemId: normalizeItemId(value.itemId),
    item: normalizeItem(value.item),
  });
}

function normalizeDeleteIntent(value) {
  requireExactKeys(value, ["operationId", "expectedRevision", "itemId"]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    itemId: normalizeItemId(value.itemId),
  });
}

function normalizePlacement(value, action) {
  requireExactKeys(value, [
    "itemId",
    "audience",
    "priceAtomic",
    "availableStock",
  ]);
  const itemId = normalizeItemId(value.itemId);
  const audience = value.audience;
  const priceAtomic = value.priceAtomic;
  const availableStock = value.availableStock;

  if (["publish", "change-audience"].includes(action)) {
    if (
      !PUBLICATION_AUDIENCES.has(audience) ||
      priceAtomic !== null ||
      availableStock !== null
    ) {
      fail("invalid-argument");
    }
  } else if (action === "list") {
    if (audience !== null) fail("invalid-argument");
    normalizeAtomicAmount(priceAtomic);
    normalizeStock(availableStock);
  } else if (
    audience !== null ||
    priceAtomic !== null ||
    availableStock !== null
  ) {
    fail("invalid-argument");
  }

  return Object.freeze({ itemId, audience, priceAtomic, availableStock });
}

function normalizePlacementRevision(value) {
  requireExactKeys(value, ["itemId", "publication", "listing"]);
  return Object.freeze({
    itemId: normalizeItemId(value.itemId),
    publication: normalizeRevision(value.publication),
    listing: normalizeRevision(value.listing),
  });
}

function normalizePlacementBatchIntent(value, { moderation = false } = {}) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "orgId",
    "action",
    "placements",
  ]);
  if (
    !PLACEMENT_ACTIONS.has(value.action) ||
    (moderation && !["withdraw", "pause"].includes(value.action)) ||
    !Array.isArray(value.placements) ||
    value.placements.length < 1 ||
    value.placements.length > PAGE_SIZE ||
    !Array.isArray(value.expectedRevision) ||
    value.expectedRevision.length !== value.placements.length
  ) {
    fail("invalid-argument");
  }
  const placements = value.placements.map((entry) =>
    normalizePlacement(entry, value.action)
  );
  const expectedRevision = value.expectedRevision.map(
    normalizePlacementRevision
  );
  const itemIds = placements.map(({ itemId }) => itemId);
  if (
    new Set(itemIds).size !== itemIds.length ||
    new Set(expectedRevision.map(({ itemId }) => itemId)).size !==
      expectedRevision.length ||
    itemIds.some((itemId, index) => expectedRevision[index].itemId !== itemId)
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: Object.freeze(expectedRevision),
    orgId: normalizeOrgId(value.orgId),
    action: value.action,
    placements: Object.freeze(placements),
  });
}

function normalizeLeaveCleanupIntent(value) {
  requireExactKeys(value, ["operationId", "expectedRevision", "orgId"]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    orgId: normalizeOrgId(value.orgId),
  });
}

function normalizeListOptions(value = {}) {
  if (!hasOnlyKeys(value, ["cursor", "limit"])) fail("invalid-argument");
  return Object.freeze({
    cursor: normalizeCursor(value.cursor ?? null),
    limit: normalizePageSize(value.limit ?? PAGE_SIZE),
  });
}

function normalizeFeedOptions(value) {
  if (!hasOnlyKeys(value, ["scope", "orgId", "cursor", "limit"])) {
    fail("invalid-argument");
  }
  if (!FEED_SCOPES.has(value.scope)) fail("invalid-argument");
  const orgId = value.orgId ?? null;
  const requiresOrganisation = new Set([
    "organisations",
    "organisation-public",
  ]).has(value.scope);
  if (
    (requiresOrganisation && orgId === null) ||
    (!requiresOrganisation && orgId !== null)
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    schemaVersion: PLACEMENT_READ_SCHEMA_VERSION,
    scope: value.scope,
    orgId: orgId === null ? null : normalizeOrgId(orgId),
    cursor: normalizeCursor(value.cursor ?? null),
    limit: normalizePageSize(value.limit ?? PAGE_SIZE),
  });
}

function normalizeOrganisation(value) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["orgId", "publicIdentifier", "name", "avatarAssetId"],
    code
  );
  if (
    typeof value.publicIdentifier !== "string" ||
    !PUBLIC_IDENTIFIER_PATTERN.test(value.publicIdentifier)
  ) {
    fail(code);
  }
  return Object.freeze({
    orgId: normalizeOrgId(value.orgId, code),
    publicIdentifier: value.publicIdentifier,
    name: normalizeText(value.name, {
      minimum: 2,
      maximum: 80,
      trim: false,
      code,
    }),
    avatarAssetId:
      value.avatarAssetId === null
        ? null
        : normalizeAssetId(value.avatarAssetId, code),
  });
}

function normalizePublicListing(value) {
  if (value === null) return null;
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["currencyCode", "currencySymbol", "priceAtomic", "tradeScope", "availableStock"],
    code
  );
  if (
    typeof value.currencyCode !== "string" ||
    !CURRENCY_CODE_PATTERN.test(value.currencyCode) ||
    typeof value.currencySymbol !== "string" ||
    !CURRENCY_SYMBOL_PATTERN.test(value.currencySymbol) ||
    !TRADE_SCOPES.has(value.tradeScope)
  ) {
    fail(code);
  }
  return Object.freeze({
    currencyCode: value.currencyCode,
    currencySymbol: value.currencySymbol,
    priceAtomic: normalizeAtomicAmount(value.priceAtomic, { code }),
    tradeScope: value.tradeScope,
    availableStock: normalizeStock(value.availableStock, { code }),
  });
}

function normalizePlacementPresentation(value) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "itemId",
      "kind",
      "title",
      "summary",
      "mediaAssetIds",
      "organisation",
      "publication",
      "listing",
    ],
    code
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !ITEM_KINDS.has(value.kind) ||
    !hasExactKeys(value.publication, ["audience", "publishedAt"]) ||
    !PUBLICATION_AUDIENCES.has(value.publication.audience) ||
    !Number.isSafeInteger(value.publication.publishedAt) ||
    value.publication.publishedAt < 0
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    itemId: normalizeItemId(value.itemId, code),
    kind: value.kind,
    title: normalizeText(value.title, {
      minimum: 1,
      maximum: 120,
      trim: false,
      code,
    }),
    summary: normalizeText(value.summary, {
      maximum: MAX_ITEM_SUMMARY_LENGTH,
      trim: false,
      code,
    }),
    mediaAssetIds: normalizeMediaAssetIds(value.mediaAssetIds, code),
    organisation: normalizeOrganisation(value.organisation),
    // publishedAt is the placement's fixed position in the chronological
    // timeline: epoch milliseconds of its first publication.
    publication: Object.freeze({
      audience: value.publication.audience,
      publishedAt: value.publication.publishedAt,
    }),
    listing: normalizePublicListing(value.listing),
  });
}

function normalizeOwnItem(value) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "itemId",
      "kind",
      "title",
      "summary",
      "mediaAssetIds",
      "originOrgId",
      "revision",
    ],
    code
  );
  if (value.schemaVersion !== SCHEMA_VERSION || !ITEM_KINDS.has(value.kind)) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    itemId: normalizeItemId(value.itemId, code),
    kind: value.kind,
    title: normalizeText(value.title, {
      minimum: 1,
      maximum: 120,
      trim: false,
      code,
    }),
    summary: normalizeText(value.summary, {
      maximum: MAX_ITEM_SUMMARY_LENGTH,
      trim: false,
      code,
    }),
    mediaAssetIds: normalizeMediaAssetIds(value.mediaAssetIds, code),
    originOrgId:
      value.originOrgId === null
        ? null
        : normalizeOrgId(value.originOrgId, code),
    revision: normalizeRevision(value.revision, { minimum: 1, code }),
  });
}

function normalizeOwnListing(value) {
  if (value === null) return null;
  const code = "invalid-response";
  requireExactKeys(
    value,
    [
      "state",
      "currencyCode",
      "priceAtomic",
      "tradeScope",
      "availableStock",
      "revision",
      "stockRevision",
    ],
    code
  );
  if (
    !LISTING_STATES.has(value.state) ||
    typeof value.currencyCode !== "string" ||
    !CURRENCY_CODE_PATTERN.test(value.currencyCode) ||
    !TRADE_SCOPES.has(value.tradeScope)
  ) {
    fail(code);
  }
  const availableStock = normalizeStock(value.availableStock, {
    allowZero: true,
    code,
  });
  if (
    (value.state === "active" && availableStock === 0) ||
    (value.state === "sold-out" && availableStock !== 0)
  ) {
    fail(code);
  }
  return Object.freeze({
    state: value.state,
    currencyCode: value.currencyCode,
    priceAtomic: normalizeAtomicAmount(value.priceAtomic, { code }),
    tradeScope: value.tradeScope,
    availableStock,
    revision: normalizeRevision(value.revision, { minimum: 1, code }),
    stockRevision: normalizeRevision(value.stockRevision, { minimum: 1, code }),
  });
}

function normalizeOwnPublication(value, expectedItemId) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["schemaVersion", "itemId", "organisation", "publication", "listing"],
    code
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.itemId !== expectedItemId ||
    !hasExactKeys(value.publication, ["state", "audience", "revision"]) ||
    !PUBLICATION_STATES.has(value.publication.state) ||
    !PUBLICATION_AUDIENCES.has(value.publication.audience)
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    itemId: expectedItemId,
    organisation: normalizeOrganisation(value.organisation),
    publication: Object.freeze({
      state: value.publication.state,
      audience: value.publication.audience,
      revision: normalizeRevision(value.publication.revision, {
        minimum: 1,
        code,
      }),
    }),
    listing: normalizeOwnListing(value.listing),
  });
}

function normalizePageEnvelope(value, keys, request) {
  const code = "invalid-response";
  requireExactKeys(value, keys, code);
  if (
    value.schemaVersion !== request.schemaVersion ||
    typeof value.hasMore !== "boolean" ||
    (value.nextCursor !== null &&
      normalizeCursor(value.nextCursor, code) !== value.nextCursor) ||
    value.hasMore !== (value.nextCursor !== null) ||
    (request.cursor !== null && value.nextCursor === request.cursor)
  ) {
    fail(code);
  }
}

function normalizeRegistryResult(value, expectedItemId, allowedOutcomes) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["schemaVersion", "itemId", "outcome", "revision"],
    code
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !allowedOutcomes.has(value.outcome) ||
    (expectedItemId !== null && value.itemId !== expectedItemId)
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    itemId: normalizeItemId(value.itemId, code),
    outcome: value.outcome,
    revision: normalizeRevision(value.revision, { minimum: 1, code }),
  });
}

function normalizeDeleteResult(value, expectedItemId) {
  const code = "invalid-response";
  const pending = value?.outcome === "deletion-pending";
  requireExactKeys(
    value,
    pending
      ? [
          "schemaVersion",
          "itemId",
          "outcome",
          "revision",
          "withdrawnPlacements",
          "cleanupJobRef",
        ]
      : [
          "schemaVersion",
          "itemId",
          "outcome",
          "revision",
          "withdrawnPlacements",
        ],
    code
  );
  const base = normalizeRegistryResult(
    {
      schemaVersion: value.schemaVersion,
      itemId: value.itemId,
      outcome: value.outcome,
      revision: value.revision,
    },
    expectedItemId,
    new Set(["deleted", "unchanged", "deletion-pending"])
  );
  if (
    !Number.isSafeInteger(value.withdrawnPlacements) ||
    value.withdrawnPlacements < 0 ||
    value.withdrawnPlacements > PAGE_SIZE ||
    (pending &&
      (typeof value.cleanupJobRef !== "string" ||
        !ITEM_ID_PATTERN.test(value.cleanupJobRef))) ||
    (pending && value.withdrawnPlacements !== 0)
  ) {
    fail(code);
  }
  return Object.freeze({
    ...base,
    withdrawnPlacements: value.withdrawnPlacements,
    ...(pending ? { cleanupJobRef: value.cleanupJobRef } : {}),
  });
}

function normalizeBatchResult(value, request) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["schemaVersion", "orgId", "action", "outcome", "results"],
    code
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.orgId !== request.orgId ||
    value.action !== request.action ||
    !["applied", "partial", "unchanged"].includes(value.outcome) ||
    !Array.isArray(value.results) ||
    value.results.length !== request.placements.length
  ) {
    fail(code);
  }
  const results = value.results.map((result, index) => {
    requireExactKeys(
      result,
      ["itemId", "status", "publicationRevision", "listingRevision"],
      code
    );
    if (
      result.itemId !== request.placements[index].itemId ||
      !["changed", "unchanged", "not-eligible"].includes(result.status)
    ) {
      fail(code);
    }
    const notEligible = result.status === "not-eligible";
    if (
      notEligible !==
      (result.publicationRevision === null && result.listingRevision === null)
    ) {
      fail(code);
    }
    return Object.freeze({
      itemId: result.itemId,
      status: result.status,
      publicationRevision: notEligible
        ? null
        : normalizeRevision(result.publicationRevision, { code }),
      listingRevision: notEligible
        ? null
        : normalizeRevision(result.listingRevision, { code }),
    });
  });
  const changed = results.filter(({ status }) => status === "changed").length;
  const ineligible = results.filter(
    ({ status }) => status === "not-eligible"
  ).length;
  const expectedOutcome =
    changed > 0 && ineligible > 0
      ? "partial"
      : changed > 0
      ? "applied"
      : "unchanged";
  if (value.outcome !== expectedOutcome) fail(code);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    orgId: value.orgId,
    action: value.action,
    outcome: value.outcome,
    results: Object.freeze(results),
  });
}

function normalizeCleanupResult(value, expectedOrgId) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["schemaVersion", "orgId", "outcome", "affectedPlacements"],
    code
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.orgId !== expectedOrgId ||
    !["withdrawn", "unchanged"].includes(value.outcome) ||
    !Number.isSafeInteger(value.affectedPlacements) ||
    value.affectedPlacements < 0 ||
    value.affectedPlacements > PAGE_SIZE ||
    (value.outcome === "withdrawn") !== value.affectedPlacements > 0
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    orgId: value.orgId,
    outcome: value.outcome,
    affectedPlacements: value.affectedPlacements,
  });
}

export function createItemAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  readsEnabled = isItemAuthorityReadsFeatureEnabled,
  commandsEnabled = isItemAuthorityCommandsFeatureEnabled,
} = {}) {
  const callables = new Map();
  const requireFeature = (command) => {
    if (command ? !commandsEnabled : !readsEnabled) fail("feature-disabled");
  };
  const callableFor = (name) => {
    if (!callables.has(name)) {
      callables.set(name, callableFactory(functionsInstance, name));
    }
    return callables.get(name);
  };
  const captureAuthContext = ({ authenticated }) => {
    const actor = authInstance?.currentUser ?? null;
    const uid = actor?.uid ?? null;
    if (
      uid !== null &&
      (typeof uid !== "string" ||
        uid.length < 1 ||
        uid.length > 128 ||
        CONTROL_CHARACTERS.test(uid))
    ) {
      fail("unauthenticated");
    }
    if (authenticated && uid === null) fail("unauthenticated");
    return Object.freeze({ actor, uid });
  };
  const invoke = async (
    name,
    request,
    { command = false, authenticated = command } = {}
  ) => {
    requireFeature(command);
    const expectedAuth = captureAuthContext({ authenticated });
    try {
      const response = await withUiDeadline(callableFor(name)(request), {
        phase: command ? "changing items" : "loading items",
        timeoutMs: command ? 30_000 : 15_000,
      });
      if (
        authInstance?.currentUser !== expectedAuth.actor ||
        (authInstance?.currentUser?.uid ?? null) !== expectedAuth.uid
      ) {
        fail("stale-auth-context");
      }
      return response?.data;
    } catch (error) {
      throw normalizeError(error);
    }
  };

  return Object.freeze({
    async createOwnItem(intent) {
      requireFeature(true);
      const request = normalizeCreateIntent(intent);
      const value = await invoke("createOwnItem", request, { command: true });
      return normalizeRegistryResult(value, null, new Set(["created"]));
    },

    async updateOwnItem(intent) {
      requireFeature(true);
      const request = normalizeUpdateIntent(intent);
      const value = await invoke("updateOwnItem", request, { command: true });
      return normalizeRegistryResult(
        value,
        request.itemId,
        new Set(["updated", "unchanged"])
      );
    },

    async deleteOwnItem(intent) {
      requireFeature(true);
      const request = normalizeDeleteIntent(intent);
      const value = await invoke("deleteOwnItem", request, { command: true });
      return normalizeDeleteResult(value, request.itemId);
    },

    async mutateOwnPlacements(intent) {
      requireFeature(true);
      const request = normalizePlacementBatchIntent(intent);
      const value = await invoke("mutateOwnPlacements", request, {
        command: true,
      });
      return normalizeBatchResult(value, request);
    },

    async moderateOrganisationPlacements(intent) {
      requireFeature(true);
      const request = normalizePlacementBatchIntent(intent, {
        moderation: true,
      });
      const value = await invoke("moderateOrganisationPlacements", request, {
        command: true,
      });
      return normalizeBatchResult(value, request);
    },

    async cleanupOwnPlacementsOnLeave(intent) {
      requireFeature(true);
      const request = normalizeLeaveCleanupIntent(intent);
      const value = await invoke("cleanupOwnPlacementsOnLeave", request, {
        command: true,
      });
      return normalizeCleanupResult(value, request.orgId);
    },

    async getItemPlacement(options) {
      requireFeature(false);
      requireExactKeys(options, ["itemId", "orgId"]);
      const request = Object.freeze({
        schemaVersion: PLACEMENT_READ_SCHEMA_VERSION,
        itemId: normalizeItemId(options.itemId),
        orgId: normalizeOrgId(options.orgId),
      });
      const value = await invoke("getItemPlacement", request);
      requireExactKeys(value, ["schemaVersion", "item"], "invalid-response");
      if (value.schemaVersion !== PLACEMENT_READ_SCHEMA_VERSION) fail("invalid-response");
      const item = normalizePlacementPresentation(value.item);
      if (
        item.itemId !== request.itemId ||
        item.organisation.orgId !== request.orgId
      ) {
        fail("invalid-response");
      }
      return item;
    },

    async listOwnItems(options = {}) {
      requireFeature(false);
      const normalized = normalizeListOptions(options);
      const request = Object.freeze({
        schemaVersion: SCHEMA_VERSION,
        ...normalized,
      });
      const value = await invoke("listOwnItems", request, {
        authenticated: true,
      });
      normalizePageEnvelope(
        value,
        ["schemaVersion", "items", "hasMore", "nextCursor"],
        request
      );
      if (!Array.isArray(value.items) || value.items.length > request.limit) {
        fail("invalid-response");
      }
      const items = value.items.map(normalizeOwnItem);
      if (new Set(items.map(({ itemId }) => itemId)).size !== items.length) {
        fail("invalid-response");
      }
      return Object.freeze({
        items: Object.freeze(items),
        hasMore: value.hasMore,
        nextCursor: value.nextCursor,
      });
    },

    async listOwnItemPublications(options) {
      requireFeature(false);
      if (!hasOnlyKeys(options, ["itemId", "cursor", "limit"])) {
        fail("invalid-argument");
      }
      const page = normalizeListOptions({
        cursor: options.cursor ?? null,
        limit: options.limit ?? PAGE_SIZE,
      });
      const request = Object.freeze({
        schemaVersion: SCHEMA_VERSION,
        itemId: normalizeItemId(options.itemId),
        ...page,
      });
      const value = await invoke("listOwnItemPublications", request, {
        authenticated: true,
      });
      normalizePageEnvelope(
        value,
        ["schemaVersion", "itemId", "publications", "hasMore", "nextCursor"],
        request
      );
      if (
        value.itemId !== request.itemId ||
        !Array.isArray(value.publications) ||
        value.publications.length > request.limit
      ) {
        fail("invalid-response");
      }
      const publications = value.publications.map((entry) =>
        normalizeOwnPublication(entry, request.itemId)
      );
      if (
        new Set(publications.map(({ organisation }) => organisation.orgId))
          .size !== publications.length
      ) {
        fail("invalid-response");
      }
      return Object.freeze({
        itemId: request.itemId,
        publications: Object.freeze(publications),
        hasMore: value.hasMore,
        nextCursor: value.nextCursor,
      });
    },

    async listItemFeed(options) {
      requireFeature(false);
      const request = normalizeFeedOptions(options);
      const value = await invoke("listItemFeed", request, {
        authenticated: !["open", "organisation-public"].includes(request.scope),
      });
      normalizePageEnvelope(
        value,
        ["schemaVersion", "scope", "orgId", "items", "hasMore", "nextCursor"],
        request
      );
      if (
        value.scope !== request.scope ||
        value.orgId !== request.orgId ||
        !Array.isArray(value.items) ||
        value.items.length > request.limit
      ) {
        fail("invalid-response");
      }
      const items = value.items.map(normalizePlacementPresentation);
      const placementKeys = items.map(
        ({ itemId, organisation }) => `${itemId}:${organisation.orgId}`
      );
      if (
        new Set(placementKeys).size !== placementKeys.length ||
        (["organisations", "organisation-public"].includes(request.scope) &&
          items.some(
            ({ organisation }) => organisation.orgId !== request.orgId
          ))
      ) {
        fail("invalid-response");
      }
      return Object.freeze({
        scope: request.scope,
        orgId: request.orgId,
        items: Object.freeze(items),
        hasMore: value.hasMore,
        nextCursor: value.nextCursor,
      });
    },
  });
}

export const itemAuthorityService = createItemAuthorityService();

export function createItemAuthorityOperationId(
  cryptoProvider = globalThis.crypto
) {
  if (typeof cryptoProvider?.randomUUID === "function") {
    const operationId = cryptoProvider.randomUUID().replaceAll("-", "_");
    if (OPERATION_ID_PATTERN.test(operationId)) return operationId;
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    return `item_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("")}`;
  }
  fail("failed-precondition");
}

export {
  normalizeCreateIntent as normalizeCreateOwnItemIntent,
  normalizeDeleteIntent as normalizeDeleteOwnItemIntent,
  normalizeFeedOptions as normalizeItemFeedOptions,
  normalizeLeaveCleanupIntent,
  normalizePlacementBatchIntent,
  normalizeUpdateIntent as normalizeUpdateOwnItemIntent,
};
